// ============================================================================
// LE LIBELLÉ D'UN CODE NAF (APE) — dans la nomenclature dont il vient.
//
// POURQUOI DEUX NOMENCLATURES. La NAF rév. 2 attribue les codes APE jusqu'au 31 décembre 2026 ; la
// NAF 2025 les remplace le 1ᵉʳ janvier 2027, et l'INSEE recode alors toutes les entreprises. Une fiche
// client saisie aujourd'hui porte un code rév. 2, une fiche saisie demain un code 2025 : les deux
// doivent se lire. Aucun code n'est commun aux deux listes, donc un code seul dit sa nomenclature.
//
// ⚠️ UN SOUS-CHEMIN, PAS L'INDEX (`@orgazone/sync-contract/naf`) : les quelque 1 500 libellés n'ont rien
//    à faire dans le paquet que la caisse importe par son index — Metro embarquerait tout ce que
//    l'index tire, à chaque installation de la flotte, pour un écran qui vit au back-office.
// ⚠️ LES LIBELLÉS SONT CEUX DE L'INSEE, générés (`naf-data.ts`, `scripts/build-naf.mjs`) : ils ne se
//    traduisent pas, ce sont des données d'une nomenclature administrative française.
// ============================================================================
import { normalizeNaf } from './company-identity';
import { NAF_2025_LABELS, NAF_REV2_LABELS } from './naf-data';

/** La nomenclature d'un code : NAF rév. 2 (jusqu'en 2026) ou NAF 2025 (à partir de 2027). */
export type NafRevision = 'rev2' | 'naf2025';

/** Un code NAF reconnu : sa forme officielle, son libellé INSEE et sa nomenclature. */
export interface NafEntry {
  code: string;
  label: string;
  revision: NafRevision;
}

/**
 * Le libellé d'un code NAF, ou `null` s'il n'a pas la forme d'un code ou n'existe dans aucune des deux
 * nomenclatures. « 4771Z » et « 47.71z » sont lus comme « 47.71Z ».
 * ⚠️ `null` NE DIT PAS « invalide » : un code bien formé mais inconnu peut venir d'une nomenclature
 *    plus ancienne ; c'est à l'écran de dire qu'il ne le connaît pas, sans le refuser.
 */
export function nafLabel(code: string): NafEntry | null {
  const normalized = normalizeNaf(code);
  if (!normalized) return null;
  const rev2 = NAF_REV2_LABELS[normalized];
  if (rev2 !== undefined) return { code: normalized, label: rev2, revision: 'rev2' };
  const naf2025 = NAF_2025_LABELS[normalized];
  if (naf2025 !== undefined) return { code: normalized, label: naf2025, revision: 'naf2025' };
  return null;
}
